import { MdClose } from 'react-icons/md';
import { Portal } from '../../components/Modal';
import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { format } from 'date-fns/esm';
import { parseISO } from 'date-fns';
import { DEFAULT_PROFILE_PIC } from '../../assets/icons';
import { Button } from '@wot/components';
import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';
import Chip from '../../components/Chip';
import ticketsApi, { Ticket as TicketType } from '../../api/tickets';

export default function TicketDetail() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState<TicketType | null>(null);
  const [loading, setLoading] = useState(true);
  const { ticketId } = useParams();

  useEffect(() => {
    if (ticketId) {
      setLoading(true);
      ticketsApi.getById(ticketId)
        .then((response) => {
          // Handle different response structures
          const ticketData = response.data || response;
          if (ticketData && typeof ticketData === 'object') {
            setTicket(ticketData);
          } else {
            throw new Error('Invalid ticket data received');
          }
        })
        .catch((error) => {
          console.error('Error fetching ticket:', error);
          toast.error('Failed to load ticket details');
          navigate(-1);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [ticketId, navigate]);

  const handleClose = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <Portal>
        <div className="relative w-full h-screen flex justify-center -z-10">
          <div onClick={() => handleClose()} className="absolute top-0 left-0 w-full h-full bg-black/70 -z-10"></div>
          <div className="w-full h-full mx-auto my-auto flex justify-end">
            <div className="w-[40%] h-full flex items-center justify-center bg-white rounded-l-2xl">
              <p>Loading ticket details...</p>
            </div>
          </div>
        </div>
      </Portal>
    );
  }

  if (!ticket) {
    return (
      <Portal>
        <div className="relative w-full h-screen flex justify-center -z-10">
          <div onClick={() => handleClose()} className="absolute top-0 left-0 w-full h-full bg-black/70 -z-10"></div>
          <div className="w-full h-full mx-auto my-auto flex justify-end">
            <div className="w-[40%] h-full flex items-center justify-center bg-white rounded-l-2xl">
              <p>Ticket not found</p>
            </div>
          </div>
        </div>
      </Portal>
    );
  }

  const updateTicketStatus = (status: 'active' | 'in-progress' | 'resolved') => {
    if (!ticket) return;
    
    ticketsApi.update({ _id: ticket._id, data: { status } })
      .then(() => {
        toast.success(`Ticket status updated to ${status}`);
        queryClient.invalidateQueries({ queryKey: ['tickets'] });
        navigate(-1);
      })
      .catch((error) => {
        console.error('Error updating ticket:', error);
        toast.error('Failed to update ticket status');
      });
  };

  const addToEnquiry = () => {
    if (!ticket) return;
    
    // This functionality would need to be implemented based on your enquiry API
    const enquiryData = {
      name: ticket.fullName,
      email: ticket.email,
      subject: ticket.subject,
      description: ticket.description,
      type: 'support',
      status: 'active',
      phoneNumber: ticket.phoneNumber,
      topic: ticket.topic,
      priority: ticket.priority,
      createdAt: ticket.createdAt
    };

    // In real implementation, this would make an API call to your enquiry endpoint
    setTimeout(() => {
      toast.success(`Ticket added to enquiries for Ticket ID: ${ticket._id}`);
      queryClient.invalidateQueries({ queryKey: ['enquiries'] });
      navigate(-1);
    }, 500);
  };

  const getPriorityState = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
        return 'pending';
      default:
        return 'pending';
    }
  };

  const getStatusState = (status: string) => {
    switch (status) {
      case 'active':
        return 'warning';
      case 'in-progress':
        return 'pending';
      case 'resolved':
        return 'success';
      default:
        return 'pending';
    }
  };

  return (
    <Portal>
      <div className="relative w-full h-screen flex justify-center -z-10">
        <div onClick={() => handleClose()} className="absolute top-0 left-0 w-full h-full bg-black/70 -z-10"></div>
        <div className="w-full h-full mx-auto my-auto flex justify-end">
          <div className="w-[40%] h-full flex flex-col gap-5 bg-white rounded-l-2xl rounded-bl-2xl p-7 overflow-y-scroll">
            <div className="w-full flex flex-col gap-3">
              <div className="w-full flex items-center justify-between">
                <h1 className="text-lg font-semibold">Ticket {ticket?._id || 'N/A'}</h1>
                <MdClose className="text-2xl cursor-pointer" onClick={() => handleClose()} />
              </div>

              <div className="flex gap-1">
                <p className="text-sm">Status: </p>
                <div className="flex items-center">
                  <Chip state={getStatusState(ticket.status || 'active')}>
                    <p className="text-sm font-semibold capitalize">{ticket.status || 'active'}</p>
                  </Chip>
                </div>
              </div>

              <div className="flex flex-col">
                <p className="text-sm">Date</p>
                <p className="text-sm font-medium">
                  {ticket.createdAt ? format(parseISO(ticket.createdAt), 'd LLL, yyyy') : 'N/A'}
                </p>
              </div>

              <div className="flex flex-col">
                <p className="text-sm">Priority</p>
                <div className="flex items-center mt-1">
                  <Chip state={getPriorityState(ticket.priority)}>
                    <p className="text-sm font-semibold capitalize">{ticket.priority}</p>
                  </Chip>
                </div>
              </div>

              <div className="flex flex-col">
                <p className="text-sm">Ticket Raised By</p>
                <p className="text-sm font-medium">{ticket.fullName}</p>
              </div>

              <div className="flex flex-col">
                <p className="text-sm">Email</p>
                <p className="text-sm font-medium">{ticket.email}</p>
              </div>

              <div className="flex flex-col">
                <p className="text-sm">Phone Number</p>
                <p className="text-sm font-medium">{ticket.phoneNumber}</p>
              </div>

              <div className="flex flex-col">
                <p className="text-sm">Subject</p>
                <p className="text-sm font-medium">{ticket.subject}</p>
              </div>

              <div className="flex flex-col">
                <p className="text-sm">Topic</p>
                <p className="text-sm font-medium">{ticket.topic}</p>
              </div>
            </div>

            <div className="w-full max-w-[180px] flex flex-col gap-4 items-center justify-center p-7 border border-primary rounded-md">
              <div className="w-10 h-10 rounded-full overflow-hidden">
                <img src={DEFAULT_PROFILE_PIC} className="w-full h-full object-cover" />
              </div>
              <h2 className="text-base font-medium">{ticket.fullName}</h2>
            
            </div>

            <div className="w-full flex flex-col gap-4 p-4 border border-primary rounded-md">
              <p className="text-sm font-medium uppercase text-primary">Description</p>
              <p className="text-sm">{ticket.description}</p>
            </div>

              <div className="w-full flex items-center gap-5 mt-auto">
              <Button type="s-outlined" block text="Cancel" onClick={() => navigate(-1)} />
              {(ticket.status || 'active') === 'active' ? (
                <Button block text="Start Progress" onClick={() => updateTicketStatus('in-progress')} />
              ) : (ticket.status || 'active') === 'in-progress' ? (
                <Button block text="Mark as Resolved" onClick={() => updateTicketStatus('resolved')} />
              ) : (
                <Button block text="Reopen Ticket" onClick={() => updateTicketStatus('active')} />
              )}
            </div>
          </div>
        </div>
      </div>
    </Portal>
  );
}